import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import SendWidget from "../common/Widgets/SendWidget";
import { handleTalkWidget } from "../common/Widgets/talk";
import Button from "../common/Button";

import { ALLOWED_APPS } from "../../config";
import {
  store,
  setCurrentPortal,
  setHideFooter,
  setPortalApps,
  setActiveApp,
} from "../../store";
import { getTeamPremium } from "../../api";
import Drawer from "./Drawer";
// import ArticleWidget from "./ArticleWidget";
import _ from "../../i18n";
import styles from "./CollaboratorPortal.module.scss";

const CollaboratorPortal = () => {
  const auth = useSelector((state) => state.auth);
  const lng = useSelector((state) => state.params.language);
  const activeApp = useSelector((state) => state.params.activeApp);
  const fiduciary = useSelector((state) => state.folder.fiduciary);
  // hubPreferences (which decides the portal state + apps) comes from
  // get-organization-settings, not from the optimistic org. While the full
  // fiduciary is loading we show a loader instead of prematurely deciding the
  // portal is inactive.
  const isFetchingFiduciary = useSelector((state) => state.folder.fetching);
  const isFolderFetched = useSelector((state) => state.folder.fetched);
  const [collaboratorEnabled, setCollaboratorEnabled] = useState(true);
  const [apps, setApps] = useState([]);
  const [hasPremiumWatch, setHasPremiumWatch] = useState(false);
  const [widgets, setWidgets] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { loggedIsAdmin } = auth;

  const canCreateNewsletter = () => {
    const user = auth.user;
    let roles = [];
    if (Array.isArray(user?.blogRole)) {
      roles = Array.isArray(user.blogRole[0]?.blogRoleInOrganization)
        ? user.blogRole[0].blogRoleInOrganization
        : user.blogRole;
    } else {
      roles = user?.blogRole?.blogRoleInOrganization ?? [];
    }
    const isChiefEditor = (roles || []).some(
      (t) => t.role === "CHIEF_EDITOR" && t.organizationId === fiduciary?.id
    );
    const hasNlTool = (fiduciary?.newsletterPreferences?.apps || []).some(
      (t) => t.type === "BLOG_NL_CREATE"
    );
    return isChiefEditor && hasNlTool;
  };

  useEffect(() => {
    if (activeApp === "FAQ") {
      dispatch(setHideFooter(true));
    } else {
      dispatch(setHideFooter(false));
    }
  }, [activeApp]);

  useEffect(async () => {
    const params = new URLSearchParams(window.location.search).get("params");
    let isNlRedirection = false;
    if (params) {
      isNlRedirection = true;
    }
    // Don't decide the portal state on the optimistic fiduciary (no
    // hubPreferences yet) — wait for the full fetchFiduciary to settle.
    if (isFetchingFiduciary || !isFolderFetched) {
      return;
    }
    if (fiduciary) {
      if (fiduciary.hubPreferences && fiduciary.hubPreferences.portals) {
        if (fiduciary.hubPreferences.portals.collaborator.enabled) {
          let config = fiduciary.hubPreferences.portals.collaborator.config;
          dispatch(setCurrentPortal("COLLABORATOR"));

          const handleWatch = async () => {
            try {
              const resp = await getTeamPremium({
                token: auth.token,
                ids: [auth.user.id],
              });
              if (
                resp.data.data &&
                resp.data.data.users &&
                resp.data.data.users.length > 0
              ) {
                return true;
              } else {
                return false;
              }
            } catch (error) {
              return false;
            }
          };

          // const tab = config
          //   ? config.apps.filter(
          //       (app) => app.enabled && ALLOWED_APPS.includes(app.id)
          //     )
          //   : [];
          let tab = [];
          if (config) {
            let watchApp = null;
            let sortingArray = [];
            config.apps.forEach((app) => {
              if (app.enabled && ALLOWED_APPS.includes(app.id)) {
                sortingArray.push(app.id);
                if (app.id === "OFFFCOURSE") {
                  watchApp = app;
                } else {
                  tab.push(app);
                }
              }
            });
            if (watchApp) {
              try {
                const result = await handleWatch();
                if (result) {
                  setHasPremiumWatch(true);
                  tab.push(watchApp);
                  tab.sort(
                    (a, b) =>
                      sortingArray.indexOf(a.id) - sortingArray.indexOf(b.id)
                  );
                  setApps(tab);
                  dispatch(setPortalApps(tab));
                } else {
                  setApps(tab);
                  dispatch(setPortalApps(tab));
                }
              } catch (e) {
                setApps(tab);
                dispatch(setPortalApps(tab));
              }
            } else {
              setApps(tab);
              dispatch(setPortalApps(tab));
            }
          }

          if (tab.length > 0) {
            setCollaboratorEnabled(true);
            // NEWSLETTER is only selectable for a CHIEF_EDITOR of the org when
            // BLOG_NL_CREATE is enabled (same rule as the apps menus), so a user
            // without that role falls back to the next app instead of landing
            // on the sending iframe.
            const tabIds = tab
              .map((a) => a.id)
              .filter((id) => id !== "NEWSLETTER" || canCreateNewsletter());
            let queryStringApp = location.search;
            if (location.search.length > 0) {
              queryStringApp = location.search.split("?")[1].includes("&")
                ? location.search.split("?")[1].split("&")[0].split("=")[1]
                : location.search.split("?")[1].split("=")[1];
            }
            if (queryStringApp.length > 0 && tabIds.includes(queryStringApp)) {
              dispatch(setActiveApp(queryStringApp));
            } else if (!activeApp || !tabIds.includes(activeApp)) {
              dispatch(
                setActiveApp(
                  isNlRedirection && tabIds.includes("NEWSLETTER")
                    ? "NEWSLETTER"
                    : tabIds[0] ?? ""
                )
              );
            }
          } else {
            setCollaboratorEnabled(false);
          }
          if (fiduciary.blogPreferences && fiduciary.blogPreferences.apps) {
            setWidgets(fiduciary.blogPreferences.apps);
          }
        } else {
          setCollaboratorEnabled(false);
        }
      } else {
        setCollaboratorEnabled(false);
      }
    }
  }, [fiduciary, location, isFetchingFiduciary, isFolderFetched, auth.user?.blogRole]);

  const renderCommunityPortal = () => {
    return (
      <div id="page">
        <Drawer activeApp={activeApp} hasPremiumWatch={hasPremiumWatch} />

        {/* {(widgets.includes("ARTICLE") || widgets.includes("TAMTAMIT")) && (
          <ArticleWidget widgets={widgets} activeApp={activeApp} />
        )} */}

        <div className={styles.widgets}>
          {widgets.includes("BLOG_NL_CREATE") && <SendWidget />}
        </div>

        {widgets.includes("TALK_FLOAT") && handleTalkWidget(store)}
      </div>
    );
  };

  if (!fiduciary) {
    return null;
  }

  // While the full fiduciary loads, render nothing here (the app already shows
  // a loader) rather than prematurely deciding the portal is inactive.
  if (isFetchingFiduciary || !isFolderFetched) {
    return null;
  }

  if (!collaboratorEnabled) {
    return (
      <div className={`${styles.emptyPage} grid-container`}>
        <h2 className={styles.emptyPage_title}>
          {_("portal_collaborator_title")}
        </h2>
        <p className={styles.emptyPage_help}>{_("portal_collaborator_txt")}</p>
        {loggedIsAdmin ? (
          <Button onClick={() => navigate("/settings")}>
            Configurer le portail collaborateur
          </Button>
        ) : null}
      </div>
    );
  }

  return renderCommunityPortal();
};

export default CollaboratorPortal;
